import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from '@paulownia/shared/lib/auth';
import { useTenant } from '@paulownia/shared/lib/tenant-context';

interface ProtectedRouteProps {
    /** If true, allows access even during onboarding (for the onboarding page itself) */
    allowDuringOnboarding?: boolean;
}

export const ProtectedRoute = ({ allowDuringOnboarding = false }: ProtectedRouteProps) => {
    const { isAuthenticated, loading, error } = useFirebaseAuth();
    const { needsOnboarding, loading: tenantLoading } = useTenant();
    const location = useLocation();

    // Show loading state while checking authentication or tenant claims
    if (loading || tenantLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    // Show error state if Firebase initialization failed
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center max-w-md">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-900 mb-2">
                            Erreur d'initialisation
                        </h2>
                        <p className="text-red-700 mb-4">
                            Une erreur s'est produite lors de l'initialisation de l'application.
                        </p>
                        <p className="text-sm text-red-600 mb-4">
                            {error.message}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                        >
                            Recharger la page
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Redirect to onboarding if user needs to complete it
    // (unless already on onboarding page or this route allows onboarding access)
    if (needsOnboarding && !allowDuringOnboarding && location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
    }

    // Redirect away from onboarding if user has already completed it
    if (!needsOnboarding && location.pathname === '/onboarding') {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
};
